export type FormatGermanNumberOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Formatiert Zahlen im deutschen Format: Tausendertrennzeichen Punkt, Dezimalkomma.
 * z. B. 1234.56 → "1.234,56"
 */
export function formatGermanNumber(
  value: number,
  options: FormatGermanNumberOptions = {},
): string {
  const n = Number.isFinite(value) ? value : 0;
  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(n);
}

function stripGermanNumberWhitespace(value: string): string {
  return value.replace(/\s/g, '').replace(/\u00a0/g, '').replace(/\u2212/g, '-');
}

function parseGermanNumberTokens(s: string): number {
  if (!s) return NaN;

  const negative = s.startsWith('-');
  const unsigned = negative ? s.slice(1) : s.startsWith('+') ? s.slice(1) : s;
  if (!unsigned) return NaN;

  let normalized = unsigned;
  if (unsigned.includes(',')) {
    normalized = unsigned.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(unsigned)) {
    normalized = unsigned.replace(/\./g, '');
  }

  const n = parseFloat(negative ? `-${normalized}` : normalized);
  return n;
}

/**
 * Parst Zahlen im deutschen Format: Tausendertrennzeichen Punkt, Dezimalkomma.
 * z. B. "13.200,00" → 13200, "1.234,56" → 1234.56, "13,5" → 13.5, "-1.234" → -1234
 */
export function parseGermanNumber(value: string): number {
  const s = stripGermanNumberWhitespace(value);
  if (!s) return 0;

  const n = parseGermanNumberTokens(s);
  return Number.isFinite(n) ? n : 0;
}

/** Unvollständige Eingabe (z. B. "12," oder "-") — noch nicht committen. */
export function isIncompleteGermanNumberInput(raw: string): boolean {
  const t = stripGermanNumberWhitespace(raw).trim();
  if (!t) return true;
  if (/^[+-]$/.test(t)) return true;
  return /[.,]$/.test(t);
}

/**
 * Wie parseGermanNumber, aber null bei leerer/unvollständiger/ungültiger Eingabe
 * (z. B. "-" während der Eingabe negativer Werte).
 */
export function parseGermanNumberOrNull(value: string): number | null {
  const trimmed = stripGermanNumberWhitespace(value).trim();
  if (!trimmed || isIncompleteGermanNumberInput(trimmed)) return null;

  const n = parseGermanNumberTokens(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Finalize on blur: trailing comma/dot counts as a whole number (e.g. "33," → 33).
 * Returns null only for a lone "+" / "-" so callers can keep the last committed value.
 */
export function parseGermanNumberOnBlur(value: string): number | null {
  const t = stripGermanNumberWhitespace(value).trim();
  if (!t) return 0;
  if (/^[+-]$/.test(t)) return null;
  const n = parseGermanNumber(t);
  return Number.isFinite(n) ? n : null;
}
